/**
 * Catalog permission wire strings (mirror backend core/permissions_catalog.Permission).
 * Use for screen access and RBAC checks in the admin UI.
 */

export const PERMISSION_CODES = {
    CONTENT_READ: 'content.read',
    CONTENT_WRITE: 'content.write',
    CONTENT_DELETE: 'content.delete',
    CONTENT_GENERATE: 'content.generate',
    CONTENT_SPEAKING_GRADE: 'content.speaking.grade',
    CONTENT_ESSAY_GRADE: 'content.essay.grade',
    CONTENT_TYPE_READ: 'content_type.read',
    CONTENT_TYPE_WRITE: 'content_type.write',
    CONTENT_TYPE_DELETE: 'content_type.delete',
    // EAA-1842: SCOPE permission — see every account's import history, not just
    // your own. Was missing from this mirror, so the permission-group dialog had no
    // label for it.
    DOCUMENT_ACCESS_ALL: 'document.access.all',
    TAG_READ: 'tag.read',
    TAG_WRITE: 'tag.write',
    TAG_DELETE: 'tag.delete',
    PROGRAM_READ: 'program.read',
    PROGRAM_ACCESS_ALL: 'program.access.all',
    PROGRAM_WRITE: 'program.write',
    PROGRAM_DELETE: 'program.delete',
    PROGRAM_GENERATE: 'program.generate',
    PROGRAM_ENROLL: 'program.enroll',
    // EAA-1930: publishing a curriculum is its own authority, split out of
    // program.write so a role can author an outline without releasing it.
    PROGRAM_PUBLISH: 'program.publish',
    INTEGRATION_READ: 'integration.read',
    INTEGRATION_WRITE: 'integration.write',
    INTEGRATION_MANAGE: 'integration.manage',
    JOB_READ: 'job.read',
    JOB_CANCEL: 'job.cancel',
    TENANT_SELF_READ: 'tenant.self.read',
    TENANT_SELF_WRITE: 'tenant.self.write',
    TENANT_TOKENS_MANAGE: 'tenant.tokens.manage',
    TENANT_SETTINGS_READ: 'tenant.settings.read',
    TENANT_JWT_EXCHANGE: 'tenant.jwt.exchange',
    // EAA-1842: writing the tenant theme is a tenant-admin authority, separate from
    // tenant.self.write (which sys-editor holds).
    TENANT_THEME_WRITE: 'tenant.theme.write',
    DASHBOARD_READ: 'dashboard.read',
    DASHBOARD_MANAGE: 'dashboard.manage',
    EMBED_LAUNCH: 'embed.launch',
    SESSION_LAUNCH: 'session.launch',
    SESSION_READ: 'session.read',
    SESSION_RW: 'session.rw',
    SESSION_UNBLOCK: 'session.unblock',
    STORAGE_READ: 'storage.read',
    STORAGE_WRITE: 'storage.write',
    STORAGE_DELETE: 'storage.delete',
    STORAGE_ADMIN: 'storage.admin',
    SYNC_STATUS_READ: 'sync.status.read',
    SYNC_STATUS_WRITE: 'sync.status.write',
    SYNC_JOB_READ: 'sync.job.read',
    SYNC_JOB_TRIGGER: 'sync.job.trigger',
    // EAA-1842: PLATFORM-scoped, never granted to a tenant group. Only platform
    // staff resolve to it, so it is how a staff-only screen is gated.
    PLATFORM_DASHBOARD_READ: 'platform.dashboard.read',
    // EAA-1212: edit platform runtime configuration (stage + confirm). Mirrors the
    // backend's Permission.CONFIG_MANAGE; gates the /settings/system screen rule.
    CONFIG_MANAGE: 'config.manage',
} as const;

export type PermissionCode = (typeof PERMISSION_CODES)[keyof typeof PERMISSION_CODES];
